/**
 * Withdrawal approvals — the screen where real money leaves.
 *
 * The lifecycle is `requested → approved → paid`, with `rejected` refunding the
 * player. Approve and pay move no money: the balance was already debited when
 * the player asked, in the same transaction that created the row, so a pending
 * withdrawal cannot be gambled twice. Only a rejection moves money, and it moves
 * it back.
 *
 * That shapes this screen. `approved` does not mean "paid" — it means an
 * operator signed off and the transfer still has to be made in the bank. Marking
 * it paid is a separate, deliberate second action recording that the external
 * leg happened. Conflating the two is how a payout gets approved, never sent,
 * and argued about a week later.
 *
 * Context sits beside the queue, not behind a click: balance, lifetime deposited
 * and withdrawn, net position and the last ledger entries. An operator releasing
 * money should be able to see, without navigating, whether this player has ever
 * deposited and whether the balance they are drawing down came from play or from
 * a deposit made an hour ago.
 */
import { useState, type ReactNode } from 'react';
import {
  approveWithdrawal,
  fetchPlayer,
  fetchSettings,
  fetchStatement,
  fetchWithdrawals,
  markWithdrawalPaid,
  rejectWithdrawal,
} from '../api/client.ts';
import { currentOperator } from '../api/auth.ts';
import type {
  AdminWithdrawal,
  Currency,
  PayoutPolicy,
  WithdrawalDestination,
  WithdrawalStatus,
} from '../api/types.ts';
import { useAsync } from '../lib/useAsync.ts';
import { format } from '../lib/money.ts';
import { absolute, age, shortTime } from '../lib/datetime.ts';
import {
  Card,
  EmptyState,
  ErrorState,
  Money,
  StatusBadge,
  TableSkeleton,
} from '../ui/primitives.tsx';
import { useConfirm, useToast } from '../ui/feedback.tsx';
import { IconOutbound, IconPeople, IconPulse, IconRefresh } from '../ui/icons.tsx';

/** The table's shape, reused by the loading skeleton so nothing jumps. */
const COLUMNS = ['text', 'text', 'num', 'num', 'num', 'text', 'text', 'num'] as const;

const FILTERS: { id: WithdrawalStatus | 'all'; label: string }[] = [
  { id: 'requested', label: 'Requested' },
  { id: 'approved', label: 'Approved · unpaid' },
  { id: 'paid', label: 'Paid' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'all', label: 'All' },
];

/** The denomination scale the server authors thresholds against (ADR-020). */
const DENOMINATION_SCALE: Record<Currency, number> = { INR: 1, USDT: 0.01, USD: 0.01, EUR: 0.01, BRL: 0.05 };

/** The four-eyes policy, or null when unset (single-approver everywhere). */
function readPayoutPolicy(settings: { key: string; value: unknown }[] | null | undefined): PayoutPolicy | null {
  const row = settings?.find((s) => s.key === 'payout_policy');
  return (row?.value as PayoutPolicy | undefined) ?? null;
}

/** True when this withdrawal is at/above the four-eyes threshold for its currency. */
function isFourEyes(row: AdminWithdrawal, policy: PayoutPolicy | null): boolean {
  if (!policy) return false;
  const threshold = policy.fourEyesAboveInr * (DENOMINATION_SCALE[row.currency] ?? 1);
  return Number(row.amount) >= threshold;
}

export function Withdrawals(): ReactNode {
  const [filter, setFilter] = useState<WithdrawalStatus | 'all'>('requested');
  const [selected, setSelected] = useState<number | null>(null);

  const withdrawals = useAsync(
    () => fetchWithdrawals(filter === 'all' ? undefined : filter),
    [filter],
  );
  // The four-eyes threshold, read once. A failure is silent — the queue still
  // works, it just cannot show the threshold chip.
  const settings = useAsync(() => fetchSettings(), []);
  const policy = readPayoutPolicy(settings.data);

  const rows = withdrawals.data ?? [];
  const active = rows.find((w) => w.withdrawalId === selected) ?? null;

  return (
    <div className="split">
      <div className="stack">
        <div className="toolbar">
          <div className="segmented" role="group" aria-label="Withdrawal status">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                className={`segment${filter === f.id ? ' is-active' : ''}`}
                aria-pressed={filter === f.id}
                onClick={() => setFilter(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <span className="card__spacer" />
          {policy ? (
            <span className="chip" title="Payouts at or above this need two different approvers (ADR-045)">
              <IconPeople size="sm" />
              Four-eyes ≥ ₹{policy.fourEyesAboveInr.toLocaleString('en-IN')}
            </span>
          ) : null}
          <button type="button" className="btn btn--sm btn--ghost" onClick={withdrawals.reload}>
            <IconRefresh size="sm" />
            Refresh
          </button>
        </div>

        <Card title="Withdrawals" icon={<IconOutbound />} count={rows.length} flush>
          {withdrawals.error && rows.length === 0 ? (
            <ErrorState
              title="The withdrawal queue did not load"
              detail={withdrawals.error}
              onRetry={withdrawals.reload}
            />
          ) : withdrawals.loading && rows.length === 0 ? (
            <TableSkeleton columns={COLUMNS} rows={8} />
          ) : rows.length === 0 ? (
            <EmptyState
              title="Nothing waiting"
              hint={
                filter === 'requested'
                  ? 'No player is waiting on a decision. Money already debited is nil.'
                  : `No withdrawal currently has the status “${filter}”.`
              }
            />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Player</th>
                    <th className="right">Amount</th>
                    <th className="right">Fee</th>
                    <th className="right">Net</th>
                    <th>Destination</th>
                    <th>Status</th>
                    <th className="right">Waiting</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={row.withdrawalId}
                      className={`row--clickable${row.withdrawalId === selected ? ' is-selected' : ''}`}
                      onClick={() => setSelected(row.withdrawalId)}
                    >
                      <td className="mono dim">{row.withdrawalId}</td>
                      <td className="nowrap cell-lead">
                        {row.username}
                        <span className="cell-sub">#{row.userId}</span>
                      </td>
                      <td className="right mono">
                        <Money value={row.amount} />
                      </td>
                      <td className="right mono dim">
                        <Money value={row.fee} />
                      </td>
                      <td className="right mono">
                        <Money value={row.netAmount} />
                      </td>
                      <td className="nowrap muted">{destinationSummary(row.destination)}</td>
                      <td>
                        <StatusBadge status={row.status} />
                        {row.status === 'requested' && row.firstApprovedBy !== null ? (
                          <span className="cell-sub" title="One signature recorded; a different approver must cast the second">
                            1 of 2 · needs a 2nd
                          </span>
                        ) : row.status === 'requested' && isFourEyes(row, policy) ? (
                          <span className="cell-sub">0 of 2</span>
                        ) : null}
                      </td>
                      <td className="right mono muted" title={absolute(row.createdAt)}>
                        {age(row.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <ReviewPanel withdrawal={active} policy={policy} onDone={() => { withdrawals.reload(); }} />
    </div>
  );
}

/**
 * One line for the queue. Every regional rail has its own case (T-122) — a
 * default branch would silently print nothing useful for a rail nobody
 * remembered to add, and the operator would be paying out blind.
 */
export function destinationSummary(destination: WithdrawalDestination | null): string {
  if (!destination) return '—';
  switch (destination.kind) {
    case 'upi':
      return `UPI ${destination.vpa}`;
    case 'bank':
      return `${destination.ifsc} / ${destination.accountNumber}`;
    case 'sepa':
      return `SEPA ${destination.iban}`;
    case 'ach':
      return `ACH ${destination.routingNumber} / ${destination.accountNumber}`;
    case 'pix':
      return `PIX ${destination.pixKeyType} ${destination.pixKey}`;
    case 'interac':
      return `Interac ${destination.email}`;
    case 'swift':
      return `SWIFT ${destination.bic} / ${destination.account}`;
    case 'crypto':
      return `${destination.chain ?? destination.network} ${destination.address.slice(0, 10)}…`;
  }
}

function ReviewPanel({
  withdrawal,
  policy,
  onDone,
}: {
  withdrawal: AdminWithdrawal | null;
  policy: PayoutPolicy | null;
  onDone: () => void;
}): ReactNode {
  const [busy, setBusy] = useState(false);
  const confirm = useConfirm();
  const toast = useToast();
  const me = currentOperator();

  const userId = withdrawal?.userId ?? 0;
  const player = useAsync(() => (userId ? fetchPlayer(userId) : Promise.resolve(null)), [userId]);
  const statement = useAsync(
    () => (userId ? fetchStatement(userId, 8) : Promise.resolve([])),
    [userId],
  );

  if (!withdrawal) {
    return (
      <Card title="Review" icon={<IconOutbound />}>
        <EmptyState
          compact
          icon={<IconOutbound size="lg" />}
          title="No request selected"
          hint="Pick a row. Balance, lifetime deposits and recent activity appear here, so the decision is made with context rather than from the amount alone."
        />
      </Card>
    );
  }

  const p = player.data;
  const entries = (statement.data ?? []).slice(0, 8);

  const facts = [
    { label: 'Player', value: `${withdrawal.username} #${withdrawal.userId}` },
    { label: 'Amount', value: <Money value={withdrawal.amount} currency /> },
    { label: 'Fee', value: <Money value={withdrawal.fee} currency /> },
    { label: 'Player receives', value: <Money value={withdrawal.netAmount} currency /> },
    { label: 'Region', value: withdrawal.region ?? '—' },
    { label: 'Destination', value: destinationSummary(withdrawal.destination) },
    { label: 'Balance now', value: p ? <Money value={p.balance} currency /> : '—' },
  ];

  async function act(
    action: 'approve' | 'paid' | 'reject',
  ): Promise<void> {
    if (!withdrawal) return;

    const spec = {
      approve: {
        title: 'Approve this withdrawal',
        body: 'Approval is the sign-off. It moves no money — the balance was already debited when the player requested it — but it clears the payout to be made in the bank.',
        confirmLabel: 'Approve',
        intent: 'default' as const,
        note: { label: 'Note for the audit trail', required: false },
      },
      paid: {
        title: 'Mark as paid',
        body: 'Record that the transfer has actually left our account. Only do this once the payment is visible in the bank — this is the record that the player was paid.',
        confirmLabel: 'Mark paid',
        intent: 'default' as const,
        note: { label: 'Bank reference / UTR of the payout', required: false },
        challenge: {
          phrase: 'PAID',
          hint: 'Typed confirmation, because there is no undo for a payout record.',
        },
      },
      reject: {
        title: 'Reject and refund',
        body: 'The full amount is returned to the player’s balance as a refund entry, and your reason is stored against the withdrawal.',
        confirmLabel: 'Reject and refund',
        intent: 'danger' as const,
        note: {
          label: 'Reason',
          required: true,
          hint: 'The player is owed an explanation, and this is the record of it.',
        },
      },
    }[action];

    const result = await confirm({ ...spec, facts });
    if (!result.confirmed) return;

    setBusy(true);
    try {
      if (action === 'approve') {
        const outcome = await approveWithdrawal(withdrawal.withdrawalId, result.note || undefined);
        toast(
          outcome.firstSignature
            ? `First signature recorded on #${withdrawal.withdrawalId}. A different approver must cast the second.`
            : `Withdrawal #${withdrawal.withdrawalId} approved.`,
        );
      } else if (action === 'paid') {
        await markWithdrawalPaid(withdrawal.withdrawalId, result.note || undefined);
        toast(`Withdrawal #${withdrawal.withdrawalId} marked paid.`);
      } else {
        const outcome = await rejectWithdrawal(withdrawal.withdrawalId, result.note);
        toast(
          outcome.refunded
            ? `Rejected and refunded. Balance is now ${format(outcome.balance)}.`
            : 'Rejected. No refund was needed.',
        );
      }
      onDone();
      player.reload();
      statement.reload();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'The action failed.', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      <Card title={`Withdrawal #${withdrawal.withdrawalId}`} icon={<IconOutbound />}>
        <div className="kv kv--spaced">
          <span className="kv__k">Requested</span>
          <span className="kv__v" title={absolute(withdrawal.createdAt)}>
            {shortTime(withdrawal.createdAt)} · {age(withdrawal.createdAt)} ago
          </span>
          <span className="kv__k">Amount</span>
          <span className="kv__v">
            <Money value={withdrawal.amount} currency />
          </span>
          <span className="kv__k">Fee</span>
          <span className="kv__v">
            <Money value={withdrawal.fee} currency />
          </span>
          <span className="kv__k">Player receives</span>
          <span className="kv__v kv__v--lead">
            <Money value={withdrawal.netAmount} currency />
          </span>
          <span className="kv__k">Status</span>
          <span className="kv__v">
            <StatusBadge status={withdrawal.status} />
          </span>
        </div>

        <div className="kv kv--spaced">
          <span className="kv__k">Send to</span>
          <span className="kv__v mono-code">{destinationDetail(withdrawal.destination)}</span>
        </div>

        {withdrawal.reviewNote ? (
          <p className="field__hint">Note: {withdrawal.reviewNote}</p>
        ) : null}

        {withdrawal.status === 'requested' && isFourEyes(withdrawal, policy) ? (
          <p className="field__hint">
            {withdrawal.firstApprovedBy === null
              ? 'Above the four-eyes threshold — your approval records the first of two signatures; a different operator must cast the second.'
              : me && withdrawal.firstApprovedBy === me.id
                ? 'You recorded the first signature. This payout now needs a different approver — you cannot sign it twice.'
                : 'One signature is in from another operator. Your approval completes the payout.'}
          </p>
        ) : null}

        <div className="row-actions row-actions--start">
          {withdrawal.status === 'requested' ? (
            <>
              <button
                type="button"
                className="btn btn--primary"
                disabled={busy || (me != null && withdrawal.firstApprovedBy === me.id)}
                title={
                  me != null && withdrawal.firstApprovedBy === me.id
                    ? 'You cast the first signature — a different approver must sign'
                    : undefined
                }
                onClick={() => act('approve')}
              >
                {isFourEyes(withdrawal, policy)
                  ? withdrawal.firstApprovedBy === null
                    ? 'Approve · 1 of 2'
                    : 'Approve · 2 of 2'
                  : 'Approve'}
              </button>
              <button
                type="button"
                className="btn btn--danger"
                disabled={busy}
                onClick={() => act('reject')}
              >
                Reject
              </button>
            </>
          ) : withdrawal.status === 'approved' ? (
            <>
              <button
                type="button"
                className="btn btn--primary"
                disabled={busy}
                onClick={() => act('paid')}
              >
                Mark paid
              </button>
              <button
                type="button"
                className="btn btn--danger"
                disabled={busy}
                onClick={() => act('reject')}
              >
                Reject
              </button>
            </>
          ) : (
            <span className="field__hint">
              {withdrawal.status === 'paid'
                ? `Paid ${shortTime(withdrawal.paidAt)}. Nothing further to do.`
                : 'Rejected and refunded. This request is closed.'}
            </span>
          )}
        </div>
      </Card>

      <Card title="Player context" icon={<IconPeople />}>
        {player.loading && !p ? (
          <TableSkeleton columns={['text', 'num']} rows={5} />
        ) : !p ? (
          <EmptyState compact title="No player record" hint="This withdrawal has no matching account." />
        ) : (
          <div className="kv">
            <span className="kv__k">Username</span>
            <span className="kv__v">{p.username}</span>
            <span className="kv__k">Status</span>
            <span className="kv__v">
              <StatusBadge status={p.status} />
            </span>
            <span className="kv__k">Balance</span>
            <span className="kv__v">
              <Money value={p.balance} currency />
            </span>
            <span className="kv__k">Deposited (lifetime)</span>
            <span className="kv__v">
              <Money value={p.depositedTotal} currency />
            </span>
            <span className="kv__k">Withdrawn (lifetime)</span>
            <span className="kv__v">
              <Money value={p.withdrawnTotal} currency />
            </span>
            <span className="kv__k">Turnover</span>
            <span className="kv__v">
              <Money value={p.turnover} currency />
            </span>
            <span className="kv__k">Net position</span>
            <span className="kv__v">
              <Money value={p.netPosition} currency colour signed />
            </span>
            <span className="kv__k">Joined</span>
            <span className="kv__v">{shortTime(p.createdAt)}</span>
          </div>
        )}
      </Card>

      <Card title="Recent activity" icon={<IconPulse />} flush>
        {statement.loading && entries.length === 0 ? (
          <TableSkeleton columns={['text', 'text', 'num', 'num']} rows={5} />
        ) : entries.length === 0 ? (
          <EmptyState
            compact
            title="No ledger entries"
            hint="This account has no recorded movement yet."
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Kind</th>
                  <th className="right">Amount</th>
                  <th className="right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.id}>
                    <td className="nowrap muted" title={absolute(entry.createdAt)}>
                      {shortTime(entry.createdAt)}
                    </td>
                    <td className="muted">{entry.kind}</td>
                    <td className="right mono">
                      <Money value={entry.amount} colour signed />
                    </td>
                    <td className="right mono dim">
                      <Money value={entry.balanceAfter} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

/** Everything an operator has to type into that region's banking app. */
function destinationDetail(destination: WithdrawalDestination | null): string {
  if (!destination) return '—';
  switch (destination.kind) {
    case 'upi':
      return `${destination.vpa}${destination.name ? ` · ${destination.name}` : ''}`;
    case 'bank':
      return `${destination.name} · ${destination.accountNumber} · ${destination.ifsc}`;
    case 'sepa':
      return `${destination.name} · ${destination.iban} · ${destination.bic}`;
    case 'ach':
      return `${destination.name} · ${destination.routingNumber} · ${destination.accountNumber} · ${destination.accountType}`;
    case 'pix':
      return `${destination.name} · ${destination.pixKeyType} · ${destination.pixKey}`;
    case 'interac':
      return `${destination.name} · ${destination.email}`;
    case 'swift':
      return `${destination.name} · ${destination.bic} · ${destination.account} · ${destination.country}`;
    case 'crypto':
      return `${destination.chain ?? destination.network} · ${destination.address}`;
  }
}
